import * as i0 from '@angular/core';
import { EventEmitter, Directive, Input, Output, NgModule } from '@angular/core';
import * as i1 from '@asymmetrik/ngx-leaflet';
import { LeafletDirectiveWrapper, LeafletModule } from '@asymmetrik/ngx-leaflet';
import * as L from 'leaflet';
import '@asymmetrik/leaflet-d3';
import { Observable } from 'rxjs';

class LeafletHexbinDirective {
    constructor(leafletDirective, zone) {
        this.zone = zone;
        // Hexbin data binding
        this.hexbinData = [];
        // Interaction events
        this.hexbinMouseover = new EventEmitter();
        this.hexbinMouseout = new EventEmitter();
        this.hexbinClick = new EventEmitter();
        // Fired when the layer is created
        this.layerReady = new EventEmitter();
        this.leafletDirective = new LeafletDirectiveWrapper(leafletDirective);
    }
    ngOnInit() {
        this.leafletDirective.init();
        const map = this.leafletDirective.getMap();
        this.zone.runOutsideAngular(() => {
            this.hexbinLayer = L.hexbinLayer(this.hexbinOptions);
        });
        // Fire the ready event
        this.layerReady.emit(this.hexbinLayer);
        // register for the hexbin events
        this.hexbinLayer.dispatch().on('mouseover', (event, data) => {
            this.zone.run(() => {
                this.hexbinMouseover.emit({ event, data });
            });
        });
        this.hexbinLayer.dispatch().on('mouseout', (event, data) => {
            this.zone.run(() => {
                this.hexbinMouseout.emit({ event, data });
            });
        });
        this.hexbinLayer.dispatch().on('click', (event, data) => {
            this.zone.run(() => {
                this.hexbinClick.emit({ event, data });
            });
        });
        this.hexbinLayer.addTo(map);
        // Initialize the data (in case the data was set before the directive was initialized)
        this.setHexbinData(this.hexbinData);
    }
    ngOnChanges(changes) {
        // Set the new data
        if (changes['hexbinData']) {
            this.setHexbinData(changes['hexbinData'].currentValue);
        }
    }
    setHexbinData(data) {
        // Only if there is a hexbinLayer do we apply the data
        if (null != this.hexbinLayer) {
            this.zone.runOutsideAngular(() => {
                this.hexbinLayer.data(data);
            });
        }
    }
}
LeafletHexbinDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.2", ngImport: i0, type: LeafletHexbinDirective, deps: [{ token: i1.LeafletDirective }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive });
LeafletHexbinDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.2", type: LeafletHexbinDirective, selector: "[leafletHexbin]", inputs: { hexbinData: ["leafletHexbin", "hexbinData"], hexbinOptions: ["leafletHexbinOptions", "hexbinOptions"] }, outputs: { hexbinMouseover: "leafletHexbinMouseover", hexbinMouseout: "leafletHexbinMouseout", hexbinClick: "leafletHexbinClick", layerReady: "leafletHexbinLayerReady" }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.2", ngImport: i0, type: LeafletHexbinDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[leafletHexbin]'
                }]
        }], ctorParameters: function () { return [{ type: i1.LeafletDirective }, { type: i0.NgZone }]; }, propDecorators: { hexbinData: [{
                type: Input,
                args: ['leafletHexbin']
            }], hexbinOptions: [{
                type: Input,
                args: ['leafletHexbinOptions']
            }], hexbinMouseover: [{
                type: Output,
                args: ['leafletHexbinMouseover']
            }], hexbinMouseout: [{
                type: Output,
                args: ['leafletHexbinMouseout']
            }], hexbinClick: [{
                type: Output,
                args: ['leafletHexbinClick']
            }], layerReady: [{
                type: Output,
                args: ['leafletHexbinLayerReady']
            }] } });

class LeafletPingDirective {
    constructor(leafletDirective, zone) {
        this.zone = zone;
        this.pingObserverReady = new EventEmitter();
        this.leafletDirective = new LeafletDirectiveWrapper(leafletDirective);
    }
    ngOnInit() {
        this.leafletDirective.init();
        const map = this.leafletDirective.getMap();
        this.zone.runOutsideAngular(() => {
            this.pingLayer = L.pingLayer(this.pingOptions).addTo(map);
            // Handle incoming ping events
            this.pingSource = Observable.create((observer) => {
                this.pingObserver = observer;
                this.pingObserverReady.emit(this.pingObserver);
            })
                .subscribe((event) => {
                if (null != event) {
                    this.ping(event.data, event.cssClass);
                }
            });
        });
    }
    /**
     * Submit a ping to the ping layer.
     *
     * @param data Contains the lat/lon information to generate the ping
     * @param cssClass Optional parameter specifying the css class to apply to the ping
     */
    ping(data, cssClass) {
        if (null != this.pingLayer) {
            this.zone.runOutsideAngular(() => {
                this.pingLayer.ping(data, cssClass);
            });
        }
    }
}
LeafletPingDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.2", ngImport: i0, type: LeafletPingDirective, deps: [{ token: i1.LeafletDirective }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive });
LeafletPingDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.2", type: LeafletPingDirective, selector: "[leafletPing]", inputs: { pingOptions: ["leafletPingOptions", "pingOptions"] }, outputs: { pingObserverReady: "leafletPingObserver" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.2", ngImport: i0, type: LeafletPingDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[leafletPing]'
                }]
        }], ctorParameters: function () { return [{ type: i1.LeafletDirective }, { type: i0.NgZone }]; }, propDecorators: { pingOptions: [{
                type: Input,
                args: ['leafletPingOptions']
            }], pingObserverReady: [{
                type: Output,
                args: ['leafletPingObserver']
            }] } });

class LeafletD3Module {
}
LeafletD3Module.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.2", ngImport: i0, type: LeafletD3Module, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
LeafletD3Module.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.2", ngImport: i0, type: LeafletD3Module, declarations: [LeafletHexbinDirective,
        LeafletPingDirective], imports: [LeafletModule], exports: [LeafletHexbinDirective,
        LeafletPingDirective] });
LeafletD3Module.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.2", ngImport: i0, type: LeafletD3Module, imports: [LeafletModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.2", ngImport: i0, type: LeafletD3Module, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        LeafletModule
                    ],
                    exports: [
                        LeafletHexbinDirective,
                        LeafletPingDirective
                    ],
                    declarations: [
                        LeafletHexbinDirective,
                        LeafletPingDirective
                    ]
                }]
        }] });

class LeafletPingEvent {
}

/**
 * Generated bundle index. Do not edit.
 */

export { LeafletD3Module, LeafletHexbinDirective, LeafletPingDirective, LeafletPingEvent };
